/**
 * URQL hooks for event CRUD operations.
 */

import { useCallback } from "react";
import { useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";
import { extractGraphQLErrorMessage } from "@/lib/graphql-utils";

// =============================================================================
// MUTATION DOCUMENTS
// =============================================================================

const UpdateEventDocument = `
  mutation UpdateEvent($id: ID!, $input: EventInput!) {
    updateEvent(id: $id, input: $input) {
      id
      title
      status
    }
  }
`;

const DeleteEventDocument = `
  mutation DeleteEvent($id: ID!) {
    deleteEvent(id: $id)
  }
`;

const DuplicateEventDocument = `
  mutation DuplicateEvent($id: ID!) {
    duplicateEvent(id: $id) {
      id
      title
    }
  }
`;

const CreateEventDocument = `
  mutation CreateEvent($input: EventInput!) {
    createEvent(input: $input) {
      id
      title
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface UpdateEventInput {
  title?: string;
  subtitle?: string | null;
  description?: string | null;
  venue?: string | null;
  address?: string | null;
  mapsUrl?: string | null;
  startsAt?: string;
  endsAt?: string | null;
  maxAttendees?: number | null;
  imageUrl?: string | null;
  bannerImageUrl?: string | null;
  videoUrl?: string | null;
  streamYoutubeUrl?: string | null;
  streamVkUrl?: string | null;
  telegramChatUrl?: string | null;
  inviteText?: string | null;
  slogan?: string | null;
  program?: string | null;
  status?: string;
  publishAt?: string | null;
  visibility?: string;
  featured?: boolean;
  genderRestriction?: string;
  minAge?: number | null;
  maxAge?: number | null;
  categoryIds?: string[];
  homeGroupId?: string | null;
  requiresRsvpConfirmation?: boolean;
  isActive?: boolean;
}

interface UpdateEventResult {
  id: string;
  title: string;
  status: string;
}

interface DuplicateEventResult {
  id: string;
  title: string;
}

interface CreateEventInput {
  title: string;
  startsAt: string;
  status?: string;
  homeGroupId?: string;
  requiresRsvpConfirmation?: boolean;
}

interface CreateEventResult {
  id: string;
  title: string;
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Hook for updating an event.
 */
export function useUpdateEvent() {
  const [result, executeMutation] = useMutation<
    { updateEvent: UpdateEventResult },
    { id: string; input: UpdateEventInput }
  >(UpdateEventDocument);

  const updateEvent = useCallback(
    async (id: string, input: UpdateEventInput): Promise<UpdateEventResult> => {
      try {
        const response = await executeMutation({ id, input });

        if (response.error) {
          const message = extractGraphQLErrorMessage(response.error, "Не удалось сохранить мероприятие");
          const err = new Error(message);
          throw err;
        }

        if (!response.data?.updateEvent) {
          throw new Error("Сервер не вернул данные");
        }

        const event = response.data.updateEvent;

        errorTracking.addBreadcrumb({
          category: "admin",
          message: "Event updated",
          data: { eventId: event.id, title: event.title },
        });

        return event;
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error("Не удалось сохранить мероприятие");
        errorTracking.captureError(error, {
          tags: { feature: "admin", action: "updateEvent" },
          extra: { eventId: id },
        });
        throw error;
      }
    },
    [executeMutation]
  );

  return {
    updateEvent,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}

/**
 * Hook for deleting an event.
 */
export function useDeleteEvent() {
  const [result, executeMutation] = useMutation<
    { deleteEvent: boolean },
    { id: string }
  >(DeleteEventDocument);

  const deleteEvent = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const response = await executeMutation({ id });

        if (response.error) {
          const message = extractGraphQLErrorMessage(response.error, "Не удалось удалить мероприятие");
          const err = new Error(message);
          throw err;
        }

        errorTracking.addBreadcrumb({
          category: "admin",
          message: "Event deleted",
          data: { eventId: id },
        });

        return response.data?.deleteEvent ?? true;
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error("Не удалось удалить мероприятие");
        errorTracking.captureError(error, {
          tags: { feature: "admin", action: "deleteEvent" },
          extra: { eventId: id },
        });
        throw error;
      }
    },
    [executeMutation]
  );

  return {
    deleteEvent,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}

/**
 * Hook for duplicating an event.
 */
export function useDuplicateEvent() {
  const [result, executeMutation] = useMutation<
    { duplicateEvent: DuplicateEventResult },
    { id: string }
  >(DuplicateEventDocument);

  const duplicateEvent = useCallback(
    async (id: string): Promise<DuplicateEventResult> => {
      try {
        const response = await executeMutation({ id });

        if (response.error) {
          const message = extractGraphQLErrorMessage(response.error, "Не удалось скопировать мероприятие");
          const err = new Error(message);
          throw err;
        }

        if (!response.data?.duplicateEvent) {
          throw new Error("Сервер не вернул данные");
        }

        const event = response.data.duplicateEvent;

        errorTracking.addBreadcrumb({
          category: "admin",
          message: "Event duplicated",
          data: { originalId: id, newId: event.id },
        });

        return event;
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error("Не удалось скопировать мероприятие");
        errorTracking.captureError(error, {
          tags: { feature: "admin", action: "duplicateEvent" },
          extra: { eventId: id },
        });
        throw error;
      }
    },
    [executeMutation]
  );

  return {
    duplicateEvent,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}

/**
 * Hook for creating an event.
 */
export function useCreateEvent() {
  const [result, executeMutation] = useMutation<
    { createEvent: CreateEventResult },
    { input: CreateEventInput }
  >(CreateEventDocument);

  const createEvent = useCallback(
    async (input: CreateEventInput): Promise<CreateEventResult> => {
      try {
        const response = await executeMutation({ input });

        if (response.error) {
          const message = extractGraphQLErrorMessage(response.error, "Не удалось создать мероприятие");
          const err = new Error(message);
          throw err;
        }

        if (!response.data?.createEvent) {
          throw new Error("Сервер не вернул данные");
        }

        const event = response.data.createEvent;

        errorTracking.addBreadcrumb({
          category: "admin",
          message: "Event created",
          data: { eventId: event.id, title: event.title },
        });

        return event;
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error("Не удалось создать мероприятие");
        errorTracking.captureError(error, {
          tags: { feature: "admin", action: "createEvent" },
        });
        throw error;
      }
    },
    [executeMutation]
  );

  return {
    createEvent,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
